<template>
  <div class="tax-history-v2">
    <v-container fluid>
      <v-toolbar color="primary" class="header-title text-white">
        <v-toolbar-title class="title">{{ $t('taxHistoryV2Title') }}</v-toolbar-title>
        <v-spacer />
        <div class="d-flex pr-4">
          <v-toolbar-title class="title">{{ today }}</v-toolbar-title>
        </div>
      </v-toolbar>

      <v-card class="card2 mt-3" data-testid="tax-history-v2-card">
        <v-card-title class="d-flex align-center flex-wrap ga-2">
          <v-icon>mdi-history</v-icon>
          <span>{{ $t('taxHistoryV2FormHistory') }}</span>
          <v-chip size="small" variant="tonal">{{ state.totalCount }}</v-chip>
          <v-spacer />
          <v-btn
            color="primary"
            variant="outlined"
            size="small"
            prepend-icon="mdi-cog"
            data-testid="tax-history-settings-btn"
            @click="settingsDialog = true"
          >
            {{ $t('taxHistorySettingsButton') }}
            <v-tooltip activator="parent" location="top">{{ $t('taxHistorySettingsTooltip') }}</v-tooltip>
          </v-btn>
          <!-- Download menu, same pattern as Tax Chat Log: Excel = the form list, ZIP = #776 answers, one .xlsx per form. -->
          <v-menu location="bottom start">
            <template #activator="{ props }">
              <v-btn
                color="success"
                variant="flat"
                size="small"
                prepend-icon="mdi-download"
                append-icon="mdi-menu-down"
                class="download-btn"
                :loading="exportLoading"
                :disabled="exportMenuDisabled"
                data-testid="tax-history-v2-download"
                v-bind="props"
              >
                {{ $t('Download') }}
              </v-btn>
            </template>
            <v-list class="download-menu" density="comfortable" max-width="420">
              <v-list-item
                prepend-icon="mdi-microsoft-excel"
                lines="two"
                data-testid="tax-history-v2-export-excel"
                @click="exportExcel"
              >
                <v-list-item-title>{{ $t('taxHistoryV2MenuExportExcel') }}</v-list-item-title>
                <v-list-item-subtitle>{{ $t('taxHistoryV2ExportExcelHint') }}</v-list-item-subtitle>
              </v-list-item>
              <!-- "too many forms" stays readable on the disabled item itself — a disabled item shows no tooltip. -->
              <v-list-item
                prepend-icon="mdi-folder-zip-outline"
                :disabled="exportTooMany"
                lines="two"
                data-testid="tax-history-v2-export-zip"
                @click="exportZip"
              >
                <v-list-item-title>{{ $t('taxHistoryV2MenuExportZip') }}</v-list-item-title>
                <v-list-item-subtitle :class="{ 'text-error': exportTooMany }">
                  {{ exportTooMany ? $t('taxHistoryV2ExportTooMany', { max: maxExportForms }) : $t('taxHistoryV2ExportZipHint') }}
                </v-list-item-subtitle>
              </v-list-item>
            </v-list>
          </v-menu>
        </v-card-title>

        <v-card-text>
          <v-alert
            v-if="state.error"
            type="error"
            variant="tonal"
            density="comfortable"
            class="mb-4"
            data-testid="tax-history-v2-error"
          >
            <div class="d-flex align-center ga-2">
              <span>{{ state.error }}</span>
              <v-spacer />
              <v-btn color="error" variant="text" size="small" data-testid="tax-history-v2-retry" @click="reload">
                {{ $t('taxHistoryV2Retry') }}
              </v-btn>
            </div>
          </v-alert>

          <v-alert
            v-if="exportError"
            type="error"
            variant="tonal"
            density="comfortable"
            closable
            class="mb-4"
            data-testid="tax-history-v2-export-error"
            @click:close="exportError = ''"
          >
            {{ exportError }}
          </v-alert>

          <!-- #776 — Date Range card, same design as Log Management (LogMessage.vue / Tax Chat Log); filters on CreatedDate (#53-PRD). -->
          <v-card variant="outlined" class="filter-card mb-3" data-testid="tax-history-v2-date-range">
            <v-card-text class="pa-3">
              <h4 class="section-title mb-2">
                <v-icon class="mr-2" color="primary" size="20">mdi-calendar-range</v-icon>
                {{ $t('dateRange') }}
              </h4>
              <v-row class="mt-1">
                <v-col cols="12" md="6">
                  <v-menu
                    v-model="startDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    location="bottom"
                    min-width="auto"
                  >
                    <template #activator="{ props }">
                      <v-text-field
                        :model-value="rangeDayLabel(startDate)"
                        :label="$t('taxHistoryV2StartDate')"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        variant="outlined"
                        density="compact"
                        color="primary"
                        class="date-field"
                        clearable
                        hide-details
                        data-testid="tax-history-v2-start-date"
                        v-bind="props"
                        @update:model-value="(val) => onDateInput('startDate', val)"
                      >
                        <template #append-inner>
                          <v-tooltip location="top" max-width="200">
                            <template #activator="{ props: tip }">
                              <v-icon size="small" color="grey-darken-1" v-bind="tip">mdi-information-outline</v-icon>
                            </template>
                            <span>{{ $t('taxHistoryV2StartDateTip') }}</span>
                          </v-tooltip>
                        </template>
                      </v-text-field>
                    </template>
                    <v-date-picker
                      :max="startDateMax"
                      color="primary"
                      @update:model-value="(val) => onDatePicked('startDate', val)"
                    />
                  </v-menu>
                </v-col>
                <v-col cols="12" md="6">
                  <v-menu
                    v-model="endDateMenu"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    location="bottom"
                    min-width="auto"
                  >
                    <template #activator="{ props }">
                      <v-text-field
                        :model-value="rangeDayLabel(endDate)"
                        :label="$t('taxHistoryV2EndDate')"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        variant="outlined"
                        density="compact"
                        color="primary"
                        class="date-field"
                        clearable
                        hide-details
                        data-testid="tax-history-v2-end-date"
                        v-bind="props"
                        @update:model-value="(val) => onDateInput('endDate', val)"
                      >
                        <template #append-inner>
                          <v-tooltip location="top" max-width="200">
                            <template #activator="{ props: tip }">
                              <v-icon size="small" color="grey-darken-1" v-bind="tip">mdi-information-outline</v-icon>
                            </template>
                            <span>{{ $t('taxHistoryV2EndDateTip') }}</span>
                          </v-tooltip>
                        </template>
                      </v-text-field>
                    </template>
                    <v-date-picker
                      :min="startDate || undefined"
                      :max="todayDate"
                      color="primary"
                      @update:model-value="(val) => onDatePicked('endDate', val)"
                    />
                  </v-menu>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>

          <v-row align="center" class="mb-2" no-gutters>
            <v-col cols="12" sm="6" md="3" class="pa-2">
              <v-select
                v-model="statusFilter"
                :items="statusOptions"
                item-title="title"
                item-value="value"
                :label="$t('taxHistoryV2Status')"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                data-testid="tax-history-v2-status-filter"
                @update:model-value="applyFilters"
              />
            </v-col>
            <v-col cols="12" sm="6" md="3" class="pa-2">
              <v-select
                v-model="yearFilter"
                :items="yearOptions"
                item-title="title"
                item-value="value"
                :label="$t('taxHistoryV2TaxYear')"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                data-testid="tax-history-v2-year-filter"
                @update:model-value="applyFilters"
              />
            </v-col>
            <v-col cols="12" sm="6" md="3" class="pa-2">
              <v-text-field
                v-model="search"
                prepend-inner-icon="mdi-magnify"
                :label="$t('taxHistoryV2SearchCifSession')"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                data-testid="tax-history-v2-search"
                @keyup.enter="applyFilters"
              />
            </v-col>
            <v-col cols="12" sm="6" md="3" class="pa-2 d-flex align-center justify-end ga-2 filter-actions">
              <v-switch
                v-model="includePlayground"
                :label="$t('taxHistoryV2Playground')"
                color="warning"
                density="compact"
                hide-details
                data-testid="tax-history-v2-playground-toggle"
                @update:model-value="applyFilters"
              />
              <v-btn
                color="primary"
                variant="flat"
                size="small"
                prepend-icon="mdi-filter-variant"
                data-testid="tax-history-v2-apply"
                @click="applyFilters"
              >
                {{ $t('taxHistoryV2Apply') }}
              </v-btn>
            </v-col>
          </v-row>

          <div class="table-wrapper">
            <v-data-table-server
              data-testid="tax-history-v2-table"
              class="my-data-table"
              density="comfortable"
              hover
              :headers="headers"
              :items="state.forms"
              :items-length="state.totalCount"
              :items-per-page="state.pageSize"
              :page="state.page"
              :loading="state.loading"
              @update:options="onOptions"
            >
              <template #item.index="{ index }">
                {{ (state.page - 1) * state.pageSize + index + 1 }}
              </template>

              <template #item.formID="{ value }">
                <span class="mono">#{{ value }}</span>
              </template>

              <template #item.userID="{ value }">
                <CopyId :value="value" :label="$t('taxHistoryV2Cif')" truncate />
              </template>

              <template #item.sessionID="{ value }">
                <CopyId :value="value" :label="$t('taxHistoryV2Session')" truncate />
              </template>

              <template #item.taxYearCode="{ item }">
                {{ item.taxYearCode || (item.yearID ? $t('taxHistoryV2Year', { id: item.yearID }) : '—') }}
              </template>

              <template #item.source="{ item }">
                <v-chip
                  size="small"
                  variant="tonal"
                  :color="item.isPlayground ? 'warning' : 'primary'"
                  :prepend-icon="item.isPlayground ? 'mdi-flask-outline' : 'mdi-chat-outline'"
                  label
                >
                  {{ item.isPlayground ? $t('taxHistoryV2Playground') : $t('taxHistoryV2Production') }}
                </v-chip>
              </template>

              <template #item.status="{ value, item }">
                <div class="d-flex align-center ga-1 flex-wrap">
                  <v-chip :color="statusColor(value)" size="small" variant="tonal" label>
                    {{ statusLabel(value) }}
                  </v-chip>
                  <v-chip
                    v-if="item.postCompleteEditStatus"
                    :color="postCompleteEditColor(item.postCompleteEditStatus)"
                    size="small"
                    variant="outlined"
                    label
                  >
                    {{ postCompleteEditLabel(item.postCompleteEditStatus) }}
                  </v-chip>
                </div>
              </template>

              <template #item.progress="{ item }">
                <span class="progress-value">{{ item.answeredCount || 0 }}/{{ item.totalRequired || 0 }}</span>
                <span class="text-caption text-medium-emphasis ml-1">{{ $t('taxHistoryV2Resolved') }}</span>
              </template>

              <template #item.skippedCount="{ value }">
                {{ value || 0 }}
              </template>

              <template #item.createdDate="{ value }">
                {{ formatDate(value) }}
              </template>

              <template #item.updatedDate="{ value }">
                {{ formatDate(value) }}
              </template>

              <template #item.actions="{ item }">
                <v-btn
                  icon="mdi-chevron-right"
                  variant="text"
                  size="small"
                  :aria-label="$t('taxHistoryV2OpenForm', { id: item.formID })"
                  :data-testid="`tax-history-v2-view-${item.formID}`"
                  @click="viewForm(item)"
                />
              </template>

              <template #no-data>
                <div class="empty-state" data-testid="tax-history-v2-empty">
                  <template v-if="state.loading">{{ $t('taxHistoryV2Loading') }}</template>
                  <!-- #776 — the page opens on "today", which is often empty: say why, and offer the way out. -->
                  <template v-else-if="hasAppliedDateRange">
                    <div>{{ $t('taxHistoryV2EmptyInRange', { from: rangeDayLabel(appliedFilters.startDate) || '…', to: rangeDayLabel(appliedFilters.endDate) || '…' }) }}</div>
                    <v-btn
                      class="mt-3"
                      size="small"
                      variant="outlined"
                      color="primary"
                      prepend-icon="mdi-calendar-remove-outline"
                      data-testid="tax-history-v2-clear-dates"
                      @click="clearDates"
                    >
                      {{ $t('taxHistoryV2ClearDates') }}
                    </v-btn>
                  </template>
                  <template v-else>{{ $t('taxHistoryV2Empty') }}</template>
                </div>
              </template>
            </v-data-table-server>
          </div>
        </v-card-text>
      </v-card>
    </v-container>

    <!-- Consent-link style + card button labels for the app's Tax History screen (loads on open). -->
    <TaxHistorySettingsDialog v-model="settingsDialog" @saved="onSettingsSaved" />

    <v-snackbar
      v-model="settingsSnackbar"
      color="success"
      location="top"
      timeout="3000"
      data-testid="tax-history-settings-snackbar"
    >
      {{ settingsSnackbarMessage }}
      <template #actions>
        <v-btn variant="text" @click="settingsSnackbar = false">{{ $t('close') }}</v-btn>
      </template>
    </v-snackbar>

    <v-snackbar
      v-model="exportSnackbar"
      color="success"
      location="top"
      timeout="3000"
      data-testid="tax-history-v2-export-snackbar"
    >
      {{ $t('taxHistoryV2ExportZipDone', { count: exportedCount }) }}
      <template #actions>
        <v-btn variant="text" @click="exportSnackbar = false">{{ $t('close') }}</v-btn>
      </template>
    </v-snackbar>
  </div>
</template>

<script>
import { state, loadForms, formatDate } from '../utils/taxHistoryV2Store.js';
import CopyId from '../components/CopyId.vue';
import TaxHistorySettingsDialog from '../components/TaxHistoryV2/TaxHistorySettingsDialog.vue';
import { taxHistoryV2Api } from '../services/taxHistoryV2Api.js';
import { collectExportRows, downloadTaxWorkbook, exportTimestamp } from '../utils/taxV2ExcelExport.js';
import { fmtThaiDate } from '../utils/thaiTime.js';

/** Same number as TaxFormV2ExportException.MaxExportForms on the API — past it the ZIP would outlive the front-door timeout. */
const MAX_EXPORT_FORMS = 2000;

/** The API's 400 codes for the export → i18n key. */
const EXPORT_ERROR_KEYS = {
  EXPORT_EMPTY: 'taxHistoryV2ExportEmpty',
  EXPORT_TOO_MANY: 'taxHistoryV2ExportTooMany',
  INVALID_DATE_RANGE: 'taxHistoryV2InvalidDateRange',
};

export default {
  name: 'TaxHistoryV2',
  components: { CopyId, TaxHistorySettingsDialog },
  data() {
    return {
      search: state.filters.search || '',
      statusFilter: state.filters.status || null,
      yearFilter: state.filters.yearId ?? null,
      includePlayground: state.filters.includePlayground === true,
      startDate: state.filters.startDate || '',
      endDate: state.filters.endDate || '',
      startDateMenu: false,
      endDateMenu: false,
      maxExportForms: MAX_EXPORT_FORMS,
      optionsReady: false,
      exportLoading: false,
      exportError: '',
      exportSnackbar: false,
      exportedCount: 0,
      appliedFilters: null,
      settingsDialog: false,
      settingsSnackbar: false,
      settingsSnackbarMessage: '',
    };
  },
  computed: {
    today() {
      const locale = this.$i18n?.locale === 'th' ? 'th-TH' : 'en-US';
      return new Date().toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric' });
    },
    state() {
      return state;
    },
    /** Today as an ICT day — the latest either picker may choose (same rule as Log Management). */
    todayDate() {
      return fmtThaiDate(new Date(), '');
    },
    startDateMax() {
      return this.endDate || this.todayDate;
    },
    hasAppliedDateRange() {
      return !!(this.appliedFilters && (this.appliedFilters.startDate || this.appliedFilters.endDate));
    },
    exportTooMany() {
      return state.totalCount > MAX_EXPORT_FORMS;
    },
    /** Both exports need a loaded, non-empty list; the ZIP item is also capped at {@link MAX_EXPORT_FORMS}. */
    exportMenuDisabled() {
      return state.loading || this.exportLoading || !!state.error || state.totalCount < 1 || !this.appliedFilters;
    },
    yearOptions() {
      const seen = new Set();
      const options = [];
      for (const form of state.forms) {
        if (form.yearID == null || seen.has(form.yearID)) continue;
        seen.add(form.yearID);
        options.push({
          title: form.taxYearCode || this.$t('taxHistoryV2Year', { id: form.yearID }),
          value: form.yearID,
        });
      }
      return options;
    },
    statusOptions() {
      return [
        { title: this.$t('taxHistoryV2All'), value: null },
        { title: this.$t('taxHistoryV2InProgress'), value: 'InProgress' },
        { title: this.$t('taxHistoryV2Completed'), value: 'Completed' },
        { title: this.$t('taxHistoryV2Abandoned'), value: 'Abandoned' },
      ];
    },
    headers() {
      return [
        { title: this.$t('taxHistoryV2No'), key: 'index', sortable: false, width: 56 },
        { title: this.$t('taxHistoryV2Form'), key: 'formID', width: 86 },
        { title: this.$t('taxHistoryV2Cif'), key: 'userID', width: 130 },
        { title: this.$t('taxHistoryV2Session'), key: 'sessionID', width: 180 },
        { title: this.$t('taxHistoryV2TaxYear'), key: 'taxYearCode', width: 120 },
        { title: this.$t('taxHistoryV2Source'), key: 'source', sortable: false, width: 130 },
        { title: this.$t('taxHistoryV2Status'), key: 'status', width: 120 },
        { title: this.$t('taxHistoryV2Progress'), key: 'progress', sortable: false, width: 130 },
        { title: this.$t('taxHistoryV2Skipped'), key: 'skippedCount', width: 80 },
        { title: this.$t('taxHistoryV2Created'), key: 'createdDate', width: 160 },
        { title: this.$t('taxHistoryV2Updated'), key: 'updatedDate', width: 160 },
        { title: this.$t('taxHistoryV2Action'), key: 'actions', sortable: false, width: 72, align: 'center' },
      ];
    },
  },
  created() {
    this.fetch(1, state.pageSize);
  },
  methods: {
    formatDate,
    statusColor(status) {
      if (status === 'Completed') return 'success';
      if (status === 'InProgress') return 'warning';
      if (status === 'Abandoned') return 'grey';
      return 'grey';
    },
    statusLabel(status) {
      const labels = {
        InProgress: 'taxHistoryV2InProgress',
        Completed: 'taxHistoryV2Completed',
        Abandoned: 'taxHistoryV2Abandoned',
      };
      return status ? this.$t(labels[status] || status) : '—';
    },
    // Second chip beside the status chip: a Completed plan the customer re-opened with «แก้ไขแผน».
    // Status stays Completed on purpose (dashboards count it); this flag is admin-only context.
    postCompleteEditColor(v) {
      if (v === 'Editing') return 'info';
      if (v === 'Edited') return 'grey';
      return 'grey';
    },
    postCompleteEditLabel(v) {
      if (v === 'Editing') return this.$t('taxHistoryV2EditingAfterComplete');
      if (v === 'Edited') return this.$t('taxHistoryV2EditedAfterComplete');
      return v;
    },
    async fetch(page, pageSize) {
      const request = {
        status: this.statusFilter || undefined,
        yearId: this.yearFilter !== null && this.yearFilter !== '' ? Number(this.yearFilter) : undefined,
        search: (this.search || '').trim() || undefined,
        includePlayground: this.includePlayground,
        startDate: this.startDate || '',
        endDate: this.endDate || '',
        page,
        pageSize,
      };
      try {
        await loadForms(request);
        this.appliedFilters = Object.freeze({
          status: request.status,
          yearId: request.yearId,
          search: request.search,
          includePlayground: request.includePlayground,
          startDate: request.startDate,
          endDate: request.endDate,
        });
      } catch {
        // The store owns the list error shown above the table.
      }
    },
    /** `2026-07-28` from whatever the date picker hands back (Date | string) — same as TaxLogV2.vue. */
    toIsoDate(value) {
      if (!value) return '';
      if (typeof value === 'string') return value.slice(0, 10);
      const d = new Date(value);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      return `${yyyy}-${mm}-${dd}`;
    },
    /** #53-PRD — the picked ICT day (`2026-10-08`) as the admin reads it, `08-10-2026`. The filter itself stays ISO. */
    rangeDayLabel(isoDay) {
      const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(isoDay || '');
      return match ? `${match[3]}-${match[2]}-${match[1]}` : '';
    },
    onDatePicked(field, value) {
      this[field] = this.toIsoDate(value);
      this[`${field}Menu`] = false;
      this.applyFilters();
    },
    /** The text field only changes through its clear (x) button — it is readonly and shows dd-mm-yyyy, not the ISO
     * value — so only an empty value means anything here. */
    onDateInput(field, value) {
      if (value || !this[field]) return;
      this[field] = '';
      this.applyFilters();
    },
    clearDates() {
      this.startDate = '';
      this.endDate = '';
      this.applyFilters();
    },
    /** The form list as shown in the table, one row per form, built in the browser (the export from before #776). */
    async exportExcel() {
      if (this.exportMenuDisabled) return;
      this.exportLoading = true;
      this.exportError = '';
      try {
        const sourceRows = await collectExportRows({
          filters: this.appliedFilters,
          rowKey: (row) => String(row.formID ?? ''),
          fetchPage: (request) => taxHistoryV2Api.list(request),
        });
        const rows = sourceRows.map((row, index) => ({
          index: index + 1,
          formID: String(row.formID ?? ''),
          userID: String(row.userID ?? ''),
          sessionID: String(row.sessionID ?? ''),
          taxYearCode: row.taxYearCode || (row.yearID ? this.$t('taxHistoryV2Year', { id: row.yearID }) : '—'),
          source: row.isPlayground ? this.$t('taxHistoryV2Playground') : this.$t('taxHistoryV2Production'),
          status: this.statusLabel(row.status),
          progress: `${Number(row.answeredCount) || 0}/${Number(row.totalRequired) || 0} ${this.$t('taxHistoryV2Resolved')}`,
          skippedCount: Number(row.skippedCount) || 0,
          createdDate: this.formatDate(row.createdDate),
          updatedDate: this.formatDate(row.updatedDate),
        }));
        await downloadTaxWorkbook({
          filename: `Tax_History_V2_${exportTimestamp()}.xlsx`,
          sheetName: 'Tax History V2',
          columns: this.headers
            .filter((column) => column.key !== 'actions')
            .map((column) => ({ header: column.title, key: column.key, width: Math.max(10, Math.round((column.width || 120) / 8)) })),
          rows,
        });
        this.exportedCount = rows.length;
        this.exportSnackbar = true;
      } catch (error) {
        this.exportError = this.$t('taxHistoryV2ExportFailed', {
          message: error?.message || this.$t('taxHistoryV2ExportUnknownError'),
        });
      } finally {
        this.exportLoading = false;
      }
    },
    /** #776 — ZIP with one answer .xlsx per form, built by the API. */
    async exportZip() {
      if (this.exportMenuDisabled || this.exportTooMany) return;
      this.exportLoading = true;
      this.exportError = '';
      const count = state.totalCount;
      try {
        const blob = await taxHistoryV2Api.exportZip({ ...this.appliedFilters, lang: this.$i18n?.locale });
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        try {
          link.href = url;
          link.download = `Tax_History_V2_${exportTimestamp()}.zip`;
          link.style.display = 'none';
          document.body.appendChild(link);
          link.click();
        } finally {
          link.remove();
          window.URL.revokeObjectURL(url);
        }
        this.exportedCount = count;
        this.exportSnackbar = true;
      } catch (error) {
        const key = EXPORT_ERROR_KEYS[error?.code];
        this.exportError = key
          ? this.$t(key, { max: MAX_EXPORT_FORMS })
          : this.$t('taxHistoryV2ExportZipFailed', {
            message: error?.httpStatus ? `HTTP ${error.httpStatus}` : (error?.message || 'Request failed'),
          });
      } finally {
        this.exportLoading = false;
      }
    },
    onOptions(options) {
      if (!this.optionsReady) {
        this.optionsReady = true;
        return;
      }
      this.fetch(options.page, options.itemsPerPage);
    },
    applyFilters() {
      this.fetch(1, state.pageSize);
    },
    reload() {
      this.fetch(state.page || 1, state.pageSize);
    },
    viewForm(item) {
      this.$router.push({ name: 'TaxHistoryV2Detail', params: { formId: item.formID } });
    },
    onSettingsSaved(message) {
      this.settingsSnackbarMessage = message;
      this.settingsSnackbar = true;
    },
  },
};
</script>

<style scoped>
/* #776 — Date Range card: same look as Log Management (LogMessage.vue). */
.filter-card {
  border-radius: 12px !important;
  transition: all 0.3s ease;
  border: 1px solid rgba(0, 0, 0, 0.12) !important;
}

.filter-card:hover {
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15) !important;
  transform: translateY(-2px);
}

.filter-actions :deep(.v-label) {
  white-space: nowrap;
}

.date-field :deep(.v-field) {
  border-radius: 8px !important;
}

.download-btn {
  text-transform: none !important;
  font-weight: 600 !important;
}

.download-menu {
  border-radius: 8px !important;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
}

.section-title {
  color: rgba(0, 0, 0, 0.87);
  font-weight: 500;
  border-bottom: 2px solid rgba(25, 118, 210, 0.2);
  padding-bottom: 8px;
}

@media (max-width: 600px) {
  .section-title {
    font-size: 1rem;
  }
}

.table-wrapper {
  max-height: 650px;
  overflow: auto;
}

.table-wrapper :deep(.my-data-table .v-table__wrapper > table) {
  width: 1664px !important;
  min-width: 1664px !important;
  max-width: 1664px !important;
  table-layout: fixed;
}

.table-wrapper :deep(.my-data-table .v-table__wrapper > table > thead > tr > :nth-child(3)),
.table-wrapper :deep(.my-data-table .v-table__wrapper > table > tbody > tr > :nth-child(3)) {
  width: 130px !important;
  min-width: 130px !important;
  max-width: 130px !important;
}

.table-wrapper :deep(.my-data-table .v-table__wrapper > table > thead > tr > :nth-child(4)),
.table-wrapper :deep(.my-data-table .v-table__wrapper > table > tbody > tr > :nth-child(4)),
.table-wrapper :deep(.my-data-table .v-table__wrapper > table > thead > tr > :nth-child(5)),
.table-wrapper :deep(.my-data-table .v-table__wrapper > table > tbody > tr > :nth-child(5)) {
  width: 120px !important;
  min-width: 120px !important;
  max-width: 120px !important;
}

.table-wrapper :deep(.v-data-table-footer) {
  position: sticky;
  right: 0;
  bottom: 0;
  z-index: 3;
  width: 100%;
  justify-content: flex-end;
  background: rgb(var(--v-theme-surface));
  box-shadow: 0 -2px 8px rgba(0, 0, 0, 0.08);
}

.table-wrapper :deep(.v-data-table-footer__items-per-page) {
  margin-inline-start: auto;
}

.table-wrapper :deep(.v-data-table-footer__pagination),
.table-wrapper :deep(.v-data-table-footer__paginationz) {
  margin-inline-start: 16px;
}

.mono,
.progress-value {
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
}

.progress-value {
  font-weight: 600;
}

.empty-state {
  padding: 32px 16px;
  color: rgba(var(--v-theme-on-surface), 0.62);
  text-align: center;
}

@media (prefers-reduced-motion: reduce) {
  .tax-history-v2 * {
    transition-duration: 0ms !important;
  }
}
</style>
